import { useState } from "react";
import { useParams, Link } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nosible/shared/components/ui/card";
import { Badge } from "@nosible/shared/components/ui/badge";
import { Button } from "@nosible/shared/components/ui/button";
import { trpc } from "@/admin/lib/trpc";
import {
  ArrowLeft,
  Webhook,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Copy,
  Check,
} from "lucide-react";

export function StripeEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [copied, setCopied] = useState(false);

  // Fetch webhook event
  const {
    data: event,
    isLoading,
    error,
  } = trpc.admin.getStripeWebhookEvent.useQuery(
    { eventId: eventId! },
    { enabled: !!eventId }
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-silica">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 p-4 rounded-lg bg-red-400/10 text-red-400">
          <AlertCircle className="h-5 w-5" />
          <p>{error.message}</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="text-chromium-mist">Event not found</div>
      </div>
    );
  }

  const formatDate = (date: Date | string | null) => {
    if (!date) return "—";
    const d = typeof date === "string" ? new Date(date) : date;
    return d.toLocaleString();
  };

  const copyPayload = async () => {
    await navigator.clipboard.writeText(JSON.stringify(event.payload, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/stripe">
            <ArrowLeft className="h-5 w-5" />
          </Link>
        </Button>
        <div className="flex items-center gap-2">
          <Webhook className="h-6 w-6 text-violet-pulse" />
          <h1 className="text-2xl font-bold text-silica font-display">Webhook Event</h1>
        </div>
      </div>

      {/* Event Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="font-mono text-lg">{event.eventType}</span>
          </CardTitle>
          <CardDescription className="font-mono text-xs">
            {event.stripeEventId}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Status */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-core">
              {event.processed ? (
                event.error ? (
                  <XCircle className="h-5 w-5 text-red-400" />
                ) : (
                  <CheckCircle2 className="h-5 w-5 text-neon-green" />
                )
              ) : (
                <Clock className="h-5 w-5 text-chromium-mist" />
              )}
              <div>
                <p className="font-medium text-silica">Status</p>
                <div className="mt-1">
                  {event.processed ? (
                    event.error ? (
                      <Badge
                        variant="outline"
                        className="text-red-400 border-red-400"
                      >
                        Error
                      </Badge>
                    ) : (
                      <Badge
                        variant="outline"
                        className="text-neon-green border-neon-green"
                      >
                        Processed
                      </Badge>
                    )
                  ) : (
                    <Badge variant="secondary">Pending</Badge>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 rounded-lg bg-slate-core">
              <Clock className="h-5 w-5 text-chromium-mist" />
              <div>
                <p className="font-medium text-silica">Internal ID</p>
                <p className="text-sm text-chromium-mist font-mono">
                  {event.id}
                </p>
              </div>
            </div>
          </div>

          {/* Timestamps */}
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-lg bg-slate-core">
              <p className="text-sm text-chromium-mist mb-1">Created At</p>
              <p className="text-silica">{formatDate(event.createdAt)}</p>
            </div>
            <div className="p-4 rounded-lg bg-slate-core">
              <p className="text-sm text-chromium-mist mb-1">Processed At</p>
              <p className="text-silica">{formatDate(event.processedAt)}</p>
            </div>
          </div>

          {/* Error Message */}
          {event.error && (
            <div className="p-4 rounded-lg bg-red-400/10 border border-red-400/20">
              <p className="text-red-400 font-medium mb-2">Error</p>
              <p className="text-sm text-red-300 font-mono">{event.error}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Raw Payload Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Raw Payload</CardTitle>
              <CardDescription>
                Full event payload received from Stripe
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={copyPayload}>
              {copied ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </>
              )}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <pre className="p-4 rounded-lg bg-slate-core overflow-auto max-h-[600px] text-sm font-mono text-silica">
            {JSON.stringify(event.payload, null, 2)}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
